import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { changeLanguage as changeAppLanguage } from "@/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { NotificationsProvider } from "@/hooks/use-notifications";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalCallCenter } from "@/components/calls/GlobalCallCenter";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FreelancerAvatar } from "@/components/FreelancerAvatar";
import { useAccountGuard } from "@/hooks/use-account-guard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Briefcase,
  LogOut,
  Settings as SettingsIcon,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    return { user: data.user };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, profile, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isRtl = i18n.dir() === "rtl";

  useAccountGuard(user?.id ?? null);

  // Portfolio referral pickup: if a new/returning client signed up after
  // clicking a freelancer's portfolio link, create an intro project and
  // route them straight into that chat.
  const referralHandledRef = useRef(false);
  useEffect(() => {
    if (!user || !profile) return;
    if (referralHandledRef.current) return;
    let ref: string | null = null;
    try {
      ref = sessionStorage.getItem("lumvio_referral");
    } catch {}
    if (!ref) return;
    // Consume the referral synchronously BEFORE any await so a re-render
    // (e.g. profile refresh) cannot race and create a duplicate intro project.
    referralHandledRef.current = true;
    try {
      sessionStorage.removeItem("lumvio_referral");
    } catch {}
    if (profile.role !== "client") return;
    (async () => {
      const { data: pid, error } = await supabase.rpc("create_intro_project_from_referral", {
        _username: ref,
      });
      if (error || !pid) return;
      navigate({ to: "/projects/$id/chat", params: { id: pid as string } });
    })();
  }, [user, profile, navigate]);

  if (loading || !user) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <NotificationsProvider>
      <SidebarProvider>
        <AppSidebar side={isRtl ? "right" : "left"} />
        <SidebarInset>
          <header className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 border-b border-border bg-background/80 px-6 backdrop-blur-md">
            <SidebarTrigger />
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label="Account menu"
                  className="ms-1 rounded-full ring-offset-background transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <FreelancerAvatar
                    path={profile?.avatar_url}
                    name={profile?.full_name || profile?.email}
                    verified={profile?.kyc_status === "verified"}
                    size="sm"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 p-2">
                  <div className="flex items-center gap-3 px-2 py-2">
                    <FreelancerAvatar
                      path={profile?.avatar_url}
                      name={profile?.full_name || profile?.email}
                      verified={profile?.kyc_status === "verified"}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">
                        {profile?.full_name || t("nav.accountDefault")}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">{profile?.email}</div>
                      <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <span className="rounded-full border border-border px-1.5 py-0.5 capitalize">
                          {t(
                            `nav.role${(profile?.role ?? "user").charAt(0).toUpperCase() + (profile?.role ?? "user").slice(1)}`,
                            { defaultValue: profile?.role || "user" },
                          )}
                        </span>
                        {profile?.kyc_status === "verified" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-500">
                            <ShieldCheck className="h-3 w-3" /> {t("nav.verified")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("nav.account")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer">
                      <UserIcon className="me-2 h-4 w-4" /> {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  {profile?.role === "freelancer" && profile?.portfolio_username ? (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/portfolio/$username"
                        params={{ username: profile.portfolio_username }}
                        className="cursor-pointer"
                      >
                        <Briefcase className="me-2 h-4 w-4" /> {t("nav.myPortfolio")}
                      </Link>
                    </DropdownMenuItem>
                  ) : profile?.role === "freelancer" ? (
                    <DropdownMenuItem asChild>
                      <Link to="/portfolio" className="cursor-pointer">
                        <Briefcase className="me-2 h-4 w-4" /> {t("nav.portfolio")}
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <SettingsIcon className="me-2 h-4 w-4" /> {t("nav.settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("nav.language")}
                  </DropdownMenuLabel>

                  <div className="flex gap-1 px-2 pb-1.5">
                    {(["en", "ar"] as const).map((lng) => {
                      const active = i18n.language?.startsWith(lng);
                      return (
                        <button
                          key={lng}
                          type="button"
                          onClick={() => {
                            void changeAppLanguage(lng);
                            try {
                              localStorage.setItem("i18nextLng", lng);
                            } catch {}
                            document.documentElement.lang = lng;
                            document.documentElement.dir = lng === "ar" ? "rtl" : "ltr";
                          }}
                          className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                            active
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted/50 text-muted-foreground hover:bg-muted"
                          }`}
                        >
                          {lng === "en" ? "English" : "العربية"}
                        </button>
                      );
                    })}
                  </div>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    onSelect={async () => {
                      await queryClient.cancelQueries();
                      queryClient.clear();
                      await supabase.auth.signOut();
                      toast.success(t("nav.signedOut"));
                      navigate({ to: "/auth", replace: true });
                    }}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="me-2 h-4 w-4" /> {t("nav.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="mx-auto w-full max-w-7xl px-6 py-8">
            <Outlet />
          </main>
          {/* Calls ring on every page, not just inside a project chat. */}
          <GlobalCallCenter />
        </SidebarInset>
      </SidebarProvider>
    </NotificationsProvider>
  );
}
